import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtMoney } from '../../lib/currency';
import { useLang } from '../../lib/lang';
import type { CategoryNode, Service } from '../../lib/types';

export interface PickedService {
  item_id: number;
  name: string;
  price: number;
  duration_minutes: number;
}

/** Category-tree + search service picker, modeled on the POS catalog tree. */
export function ServicePicker({
  branchId,
  selected,
  onToggle,
  onClose,
}: {
  branchId: number;
  selected: Map<number, PickedService>;
  onToggle: (svc: PickedService) => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useLang();
  const [openCat, setOpenCat] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const catsQ = useQuery({ queryKey: ['appt-categories', branchId], queryFn: () => api.categories(branchId) });
  const searchQ = useQuery({
    queryKey: ['appt-service-search', search],
    queryFn: () => api.services({ q: search }),
    enabled: search.trim().length >= 2,
  });
  const catServicesQ = useQuery({
    queryKey: ['appt-service-cat', openCat],
    queryFn: () => api.services({ categoryId: openCat! }),
    enabled: openCat != null,
  });

  const roots = useMemo(() => (catsQ.data ?? []).filter((c) => c.parent_id == null), [catsQ.data]);
  const childrenOf = (id: number): CategoryNode[] => (catsQ.data ?? []).filter((c) => c.parent_id === id);

  const toPicked = (s: Service): PickedService => ({
    item_id: s.item_id,
    name: s.name ?? s.code,
    price: s.price,
    duration_minutes: s.duration_minutes,
  });

  const serviceRow = (s: Service): JSX.Element => {
    const on = selected.has(s.item_id);
    return (
      <button
        key={s.item_id}
        onClick={() => onToggle(toPicked(s))}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-start text-base transition ${
          on ? 'border-brand bg-brand/5' : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
        }`}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className={`flex h-4 w-4 flex-none items-center justify-center rounded border ${on ? 'border-brand bg-brand text-white' : 'border-slate-300'}`}>
            {on && '✓'}
          </span>
          <span className="truncate font-medium text-slate-800 dark:text-slate-100">{s.name ?? s.code}</span>
        </span>
        <span className="flex-none whitespace-nowrap text-sm text-slate-400">
          {s.duration_minutes} {t({ en: 'min', ar: 'دقيقة' })} · {fmtMoney(s.price)}
        </span>
      </button>
    );
  };

  // A bottom sheet on a phone (thumb-reachable, and the keyboard pushing the
  // search field up does not shove the whole dialog off-screen), a centred
  // modal from sm. `dvh` not `vh`: with `vh` the footer sat behind mobile
  // Safari's address bar.
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      /* The picker renders inside the booking dialog's backdrop, which closes
         the dialog on click. Without stopping here, dismissing the picker also
         threw away the half-filled appointment behind it. */
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div
        className="appt-card flex max-h-[85dvh] w-full max-w-lg flex-col rounded-b-none p-4 sm:max-h-[80dvh] sm:rounded-2xl"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{t({ en: 'Services', ar: 'الخدمات' })}</h3>
          <button className="-me-1 flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>✕</button>
        </div>
        <input
          className="appt-input mb-3"
          placeholder={t({ en: 'Search services or barcode…', ar: 'ابحث عن خدمة أو باركود…' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="min-h-0 flex-1 space-y-1 overflow-auto appt-scroll pe-1">
          {search.trim().length >= 2 ? (
            (searchQ.data ?? []).length === 0 ? (
              <p className="py-6 text-center text-base text-slate-400">{searchQ.isLoading ? t({ en: 'Searching…', ar: 'جارٍ البحث…' }) : t({ en: 'No matches.', ar: 'لا نتائج.' })}</p>
            ) : (
              searchQ.data!.map(serviceRow)
            )
          ) : (
            roots.map((cat) => (
              <div key={cat.id}>
                <button
                  onClick={() => setOpenCat(openCat === cat.id ? null : cat.id)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-start text-base font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <span className="text-slate-400">{openCat === cat.id ? '▾' : '▸'}</span>
                  {cat.name ?? cat.code}
                </button>
                {openCat === cat.id && (
                  <div className="ms-4 space-y-1 border-s border-slate-200 ps-2 dark:border-slate-700">
                    {childrenOf(cat.id).map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => setOpenCat(sub.id)}
                        className="block w-full rounded px-2 py-1 text-start text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        ▸ {sub.name ?? sub.code}
                      </button>
                    ))}
                    {catServicesQ.isLoading && <p className="py-2 text-sm text-slate-400">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</p>}
                    {(catServicesQ.data ?? []).map(serviceRow)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
          <span className="text-base text-slate-500">{selected.size} {t({ en: 'selected', ar: 'محددة' })}</span>
          <button className="appt-btn-primary" onClick={onClose}>{t({ en: 'Done', ar: 'تم' })}</button>
        </div>
      </div>
    </div>
  );
}
